<template>
  <div class="view-root">
    <div class="view-header">
      <h2 class="view-title">預約連結管理</h2>
      <button class="btn btn-primary btn-sm" @click="openCreate">+ 新增預約頁面</button>
    </div>

    <div v-if="loading" class="loading-state">載入中…</div>
    <div v-else-if="!pages.length" class="empty-state">
      <div style="font-size:2.5rem;margin-bottom:12px;">📅</div>
      <div style="font-weight:700;margin-bottom:6px;">尚無預約頁面</div>
      <div style="color:var(--gray-400);font-size:.9rem;">建立您的第一個 Calendly 風格預約連結</div>
    </div>
    <div v-else class="cards-grid">
      <div v-for="p in pages" :key="p.id" class="page-card">
        <div class="page-card-header">
          <div class="page-title">{{ p.title }}</div>
          <div class="page-meta">
            <span>⏱ {{ p.duration_min }} 分鐘</span>
            <span v-if="p.location">📍 {{ p.location }}</span>
          </div>
        </div>
        <div class="page-desc" v-if="p.description">{{ p.description }}</div>
        <div class="avail-chips">
          <span v-for="a in p.availability" :key="a.weekday + a.start_time" class="chip">
            {{ weekdays[a.weekday] }} {{ a.start_time }}–{{ a.end_time }}
          </span>
          <span v-if="!p.availability?.length" style="color:var(--gray-400);font-size:.8rem;">尚未設定可用時段</span>
        </div>
        <div class="page-url">
          <span class="url-text" @click="copyUrl(p)">{{ publicUrl(p) }}</span>
          <button class="btn btn-xs btn-secondary" @click="copyUrl(p)">複製連結</button>
          <a :href="publicUrl(p)" target="_blank" class="btn btn-xs btn-secondary">預覽</a>
        </div>
        <div class="page-actions">
          <button class="btn btn-xs btn-secondary" @click="openEdit(p)">✏ 編輯</button>
          <button class="btn btn-xs" @click="openAppointments(p)"
            style="background:var(--gray-100);color:var(--gray-700);border:1px solid var(--border);">
            📋 預約紀錄
          </button>
          <button class="btn btn-xs btn-danger" @click="deletePage(p)">刪除</button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal=false">
      <div class="modal-box" style="max-width:600px;">
        <div class="modal-header">
          <h3>{{ editingId ? '編輯預約頁面' : '新增預約頁面' }}</h3>
          <button class="modal-close" @click="showModal=false">✕</button>
        </div>
        <div class="modal-body space-y-3">
          <div>
            <label class="label">頁面名稱 *</label>
            <input v-model="form.title" class="input" placeholder="例：30 分鐘諮詢">
          </div>
          <div>
            <label class="label">說明</label>
            <textarea v-model="form.description" class="input" rows="2" placeholder="預約說明（可選）"></textarea>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="label">會議時長（分鐘）</label>
              <input v-model.number="form.duration_min" type="number" min="5" max="480" class="input">
            </div>
            <div>
              <label class="label">緩衝時間（分鐘）</label>
              <input v-model.number="form.buffer_min" type="number" min="0" max="120" class="input">
            </div>
          </div>
          <div>
            <label class="label">地點 / 會議方式</label>
            <input v-model="form.location" class="input" placeholder="例：Google Meet / 辦公室 3F">
          </div>
          <div>
            <label class="label">時區</label>
            <select v-model="form.timezone" class="input">
              <option value="Asia/Taipei">亞洲/台北 (UTC+8)</option>
              <option value="Asia/Tokyo">亞洲/東京 (UTC+9)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">美東 (UTC-5)</option>
              <option value="America/Los_Angeles">美西 (UTC-8)</option>
            </select>
          </div>

          <div>
            <label class="label">可用時段設定</label>
            <div class="avail-editor">
              <div v-for="(av, i) in form.availability" :key="i" class="avail-row">
                <select v-model.number="av.weekday" class="input" style="width:90px;">
                  <option v-for="(d,j) in weekdays" :key="j" :value="j">{{ d }}</option>
                </select>
                <input v-model="av.start_time" type="time" class="input" style="width:110px;">
                <span style="color:var(--gray-400);">至</span>
                <input v-model="av.end_time" type="time" class="input" style="width:110px;">
                <button class="btn btn-xs btn-danger" @click="form.availability.splice(i,1)">✕</button>
              </div>
              <button class="btn btn-xs btn-secondary" @click="addAvail">+ 新增時段</button>
            </div>
          </div>

          <div>
            <label class="label">自訂問題（每行一題）</label>
            <textarea v-model="questionsText" class="input" rows="3"
              placeholder="例：您的公司名稱？&#10;主要需求為何？"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showModal=false">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="savePage">
            {{ saving ? '儲存中…' : (editingId ? '更新' : '建立') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Appointments Modal -->
    <div v-if="showAptModal" class="modal-overlay" @click.self="showAptModal=false">
      <div class="modal-box" style="max-width:700px;">
        <div class="modal-header">
          <h3>預約紀錄 — {{ aptPage?.title }}</h3>
          <button class="modal-close" @click="showAptModal=false">✕</button>
        </div>
        <div class="modal-body">
          <div v-if="aptLoading" class="text-center py-8">載入中…</div>
          <div v-else-if="!appointments.length" class="text-center py-8" style="color:var(--gray-400);">暫無預約紀錄</div>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>姓名</th>
                <th>Email</th>
                <th>時間</th>
                <th>狀態</th>
                <th>備註</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in appointments" :key="a.id">
                <td>{{ a.invitee_name }}</td>
                <td>{{ a.invitee_email }}</td>
                <td>{{ fmtDt(a.start_at) }}</td>
                <td>
                  <span class="badge" :class="a.status === 'confirmed' ? 'badge-success' : 'badge-gray'">
                    {{ a.status === 'confirmed' ? '已確認' : '已取消' }}
                  </span>
                </td>
                <td style="max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ a.notes }}</td>
                <td>
                  <button v-if="a.status !== 'cancelled'" class="btn btn-xs btn-danger"
                    @click="cancelApt(a)">取消</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAptModal=false">關閉</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'
import { useAppStore, ADMIN_API } from '../store/app.js'

const store = useAppStore()
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` })

const weekdays = ['週一', '週二', '週三', '週四', '週五', '週六', '週日']

const pages = ref([])
const loading = ref(false)
const showModal = ref(false)
const editingId = ref(null)
const saving = ref(false)
const questionsText = ref('')

const defaultForm = () => ({
  title: '', description: '', duration_min: 30, buffer_min: 10,
  location: '', timezone: 'Asia/Taipei', availability: [], questions: []
})
const form = reactive(defaultForm())

const showAptModal = ref(false)
const aptPage = ref(null)
const appointments = ref([])
const aptLoading = ref(false)

async function loadPages() {
  loading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/booking/pages`, { headers: authHdr() })
    pages.value = res.data || []
  } catch (e) {
    window.$toast?.error('載入失敗', e?.response?.data?.detail || e.message)
  } finally {
    loading.value = false
  }
}

function publicUrl(p) {
  return `${window.location.origin}/book/${p.slug}`
}

function copyUrl(p) {
  navigator.clipboard.writeText(publicUrl(p))
  window.$toast?.success('連結已複製')
}

function openCreate() {
  Object.assign(form, defaultForm())
  editingId.value = null
  questionsText.value = ''
  showModal.value = true
}

function openEdit(p) {
  Object.assign(form, {
    title: p.title, description: p.description || '',
    duration_min: p.duration_min, buffer_min: p.buffer_min,
    location: p.location || '', timezone: p.timezone || 'Asia/Taipei',
    availability: (p.availability || []).map(a => ({ ...a })),
  })
  questionsText.value = (p.questions || []).join('\n')
  editingId.value = p.id
  showModal.value = true
}

function addAvail() {
  form.availability.push({ weekday: 0, start_time: '09:00', end_time: '17:00' })
}

async function savePage() {
  if (!form.title.trim()) return window.$toast?.error('請輸入頁面名稱')
  saving.value = true
  try {
    const payload = {
      ...form,
      questions: questionsText.value.split('\n').map(s => s.trim()).filter(Boolean),
    }
    if (editingId.value) {
      await axios.put(`${ADMIN_API}/ai/booking/pages/${editingId.value}`, payload, { headers: authHdr() })
      window.$toast?.success('已更新')
    } else {
      await axios.post(`${ADMIN_API}/ai/booking/pages`, payload, { headers: authHdr() })
      window.$toast?.success('已建立')
    }
    showModal.value = false
    await loadPages()
  } catch (e) {
    window.$toast?.error('儲存失敗', e?.response?.data?.detail || e.message)
  } finally {
    saving.value = false
  }
}

async function deletePage(p) {
  if (!confirm(`確定刪除「${p.title}」？`)) return
  try {
    await axios.delete(`${ADMIN_API}/ai/booking/pages/${p.id}`, { headers: authHdr() })
    window.$toast?.success('已刪除')
    await loadPages()
  } catch (e) {
    window.$toast?.error('刪除失敗')
  }
}

async function openAppointments(p) {
  aptPage.value = p
  showAptModal.value = true
  aptLoading.value = true
  try {
    const res = await axios.get(`${ADMIN_API}/ai/booking/pages/${p.id}/appointments`, { headers: authHdr() })
    appointments.value = res.data || []
  } catch (e) {
    window.$toast?.error('載入預約記錄失敗')
  } finally {
    aptLoading.value = false
  }
}

async function cancelApt(a) {
  if (!confirm('確定取消此預約？')) return
  try {
    await axios.delete(`${ADMIN_API}/ai/booking/appointments/${a.id}`, { headers: authHdr() })
    a.status = 'cancelled'
    window.$toast?.success('已取消')
  } catch (e) {
    window.$toast?.error('操作失敗')
  }
}

function fmtDt(dt) {
  if (!dt) return ''
  return new Date(dt).toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

onMounted(loadPages)
</script>

<style scoped>
.view-root { padding: 24px; max-width: 1000px; margin: 0 auto; }
.view-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.view-title { font-size: 1.3rem; font-weight: 800; }
.loading-state, .empty-state { text-align: center; padding: 60px 0; color: var(--gray-400); }
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 16px; }
.page-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 18px; display: flex; flex-direction: column; gap: 10px; }
.page-card-header { display: flex; flex-direction: column; gap: 4px; }
.page-title { font-size: 1.05rem; font-weight: 700; }
.page-meta { display: flex; gap: 10px; font-size: .82rem; color: var(--gray-500); }
.page-desc { font-size: .88rem; color: var(--gray-600); }
.avail-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip { background: var(--gray-100); border-radius: 6px; padding: 2px 8px; font-size: .78rem; color: var(--gray-700); }
.page-url { display: flex; align-items: center; gap: 8px; background: var(--gray-50); border-radius: 8px; padding: 6px 10px; }
.url-text { font-size: .78rem; color: var(--brand-600); cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.page-actions { display: flex; gap: 8px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; }
.modal-box { background: var(--surface); border-radius: 16px; width: 100%; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,.2); }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 0; }
.modal-header h3 { font-size: 1.1rem; font-weight: 700; }
.modal-close { background: none; border: none; font-size: 1.1rem; cursor: pointer; color: var(--gray-400); }
.modal-body { padding: 16px 24px; }
.modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px 20px; border-top: 1px solid var(--border); }
.label { display: block; font-size: .8rem; font-weight: 700; color: var(--gray-600); margin-bottom: 4px; }
.avail-editor { display: flex; flex-direction: column; gap: 6px; }
.avail-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.data-table { width: 100%; border-collapse: collapse; font-size: .88rem; }
.data-table th { background: var(--gray-50); padding: 8px 10px; text-align: left; font-size: .78rem; font-weight: 700; color: var(--gray-600); border-bottom: 1px solid var(--border); }
.data-table td { padding: 8px 10px; border-bottom: 1px solid var(--border); }
.badge { display: inline-block; border-radius: 5px; padding: 2px 8px; font-size: .75rem; font-weight: 600; }
.badge-success { background: #dcfce7; color: #166534; }
.badge-gray { background: var(--gray-100); color: var(--gray-600); }
.space-y-3 > * + * { margin-top: 12px; }
.grid { display: grid; }
.grid-cols-2 { grid-template-columns: 1fr 1fr; }
.gap-3 { gap: 12px; }
</style>
